import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  MapPin,
  ArrowRight,
  ArrowDown,
  Laptop2,
  HeartPulse,
  Navigation,
  Salad,
  Instagram,
  Send,
  CheckCircle2,
  AlertCircle,
  X,
} from "lucide-react";
import "./App.css";
import { dict, LANGUAGES, isValidEmail } from "./i18n";
import { legalContent, legalUi } from "./legal";
import { submitNewsletter, submitContact, formatApiErrorDetail } from "./lib/api";
import logoSvg from "./assets/logo.svg";

const LOGO_URL = logoSvg;
const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/95d3a24c-d95a-4864-8e24-ee32fd7734e3/images/7037feb0d1a1565207004ba26c88ac013332a87fb679003277da39580d87e971.png";
const SUPPORT_DOG_1 =
  "https://images.unsplash.com/photo-1514571948039-d3cb9e8f9750?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxub3JkaWMlMjBkb2clMjB3aW50ZXIlMjBmb3Jlc3R8ZW58MHx8fHwxNzc5NDU5MTk0fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const SERVICE_ICONS = [Laptop2, HeartPulse, Navigation, Salad];

/* ---------- Legal Modal ---------- */
function LegalSection({ heading, body }) {
  return (
    <section data-testid={`legal-section-${heading}`}>
      <h3 className="font-display text-xl text-ink">{heading}</h3>
      <div className="mt-2 space-y-1.5 text-[15px] leading-relaxed text-ink/80 font-light">
        {body.map((line) => (
          <p key={`${heading}::${line.slice(0, 32)}`}>{line}</p>
        ))}
      </div>
    </section>
  );
}

function LegalModal({ open, onClose, lang, kind }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!kind) return null;
  const content = legalContent[lang]?.[kind] || legalContent.de[kind];
  const ui = legalUi[lang] || legalUi.de;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[60] flex items-stretch sm:items-center sm:justify-center"
          data-testid="legal-modal"
          role="dialog"
          aria-modal="true"
          aria-label={content.title}
        >
          <div
            className="absolute inset-0 bg-ink/55 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full sm:max-w-3xl sm:mx-4 bg-cream text-ink sm:rounded-3xl shadow-deep max-h-[100vh] sm:max-h-[88vh] flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 sm:px-10 py-5 border-b border-line">
              <div className="flex items-center gap-3">
                <img
                  src={LOGO_URL}
                  alt="DogCenter Lappland"
                  className="h-8 w-8 object-contain"
                />
                <div className="eyebrow text-sage">
                  {kind === "imprint" ? "Legal" : "Privacy"}
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label={ui.close}
                className="w-10 h-10 rounded-full border border-line hover:bg-muted/60 transition-colors flex items-center justify-center"
                data-testid="legal-modal-close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="overflow-y-auto px-6 sm:px-10 py-8 sm:py-10">
              <h2 className="font-display text-3xl sm:text-4xl font-light leading-tight">
                {content.title}
              </h2>
              <div className="mt-2 eyebrow text-mutedfg">{ui.updated}</div>

              <div className="mt-8 space-y-7">
                {content.sections.map((s) => (
                  <LegalSection key={s.heading} heading={s.heading} body={s.body} />
                ))}
              </div>
            </div>

            <div className="px-6 sm:px-10 py-4 border-t border-line flex justify-end">
              <button
                onClick={onClose}
                className="btn-outline"
                data-testid="legal-modal-close-bottom"
              >
                {ui.close}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------- Nav helpers ---------- */
function langPillClass(active, scrolled) {
  if (scrolled) {
    return active ? "bg-sage/20 text-ink" : "text-ink/60 hover:text-ink";
  }
  return active ? "bg-cream/15 text-cream" : "text-cream/65 hover:text-cream";
}

function LangPill({ code, label, active, scrolled, onSelect }) {
  return (
    <button
      onClick={() => onSelect(code)}
      className={`lang-pill ${langPillClass(active, scrolled)}`}
      data-testid={`lang-switch-${code}`}
      aria-label={`Switch to ${label}`}
    >
      {label}
    </button>
  );
}

/* ---------- Nav ---------- */
function Nav({ lang, setLang, t, scrolled }) {
  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div
          className={`flex items-center justify-between rounded-full px-4 sm:px-6 py-2.5 transition-all duration-500 ${
            scrolled ? "glass-light shadow-soft" : "glass-dark"
          }`}
        >
          <a
            href="#top"
            className="flex items-center gap-3"
            data-testid="nav-logo-link"
          >
            <img
              src={LOGO_URL}
              alt="DogCenter Lappland"
              className="h-10 w-10 object-contain"
            />
            <div className="hidden sm:block leading-tight">
              <div className="font-brand text-[15px] sm:text-base tracking-wide">
                <span className="font-bold text-brand-olive">DOGCENTER</span>{" "}
                <span className="font-semibold text-brand-sage">Lappland</span>
              </div>
              <div
                className={`eyebrow -mt-0.5 transition-colors duration-500 ${
                  scrolled ? "text-mutedfg" : "text-cream/60"
                }`}
              >
                AB · Sweden
              </div>
            </div>
          </a>

          <nav
            className={`hidden md:flex items-center gap-7 text-sm transition-colors duration-500 ${
              scrolled ? "text-ink/80" : "text-cream/90"
            }`}
          >
            <a
              href="#services"
              className="link-underline"
              data-testid="nav-link-services"
            >
              {t.nav.services}
            </a>
            <a
              href="#vision"
              className="link-underline"
              data-testid="nav-link-vision"
            >
              {t.nav.vision}
            </a>
            <a
              href="#contact"
              className="link-underline"
              data-testid="nav-link-contact"
            >
              {t.nav.contact}
            </a>
          </nav>

          <div className="flex items-center gap-1.5">
            {LANGUAGES.map((l) => (
              <LangPill
                key={l.code}
                code={l.code}
                label={l.label}
                active={lang === l.code}
                scrolled={scrolled}
                onSelect={setLang}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.header>
  );
}

/* ---------- Hero ---------- */
function Hero({ t }) {
  return (
    <section
      id="top"
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="hero-section"
    >
      <div
        className="absolute inset-0 bg-cover bg-center scale-[1.02]"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 hero-gradient-overlay" />
      <div className="absolute inset-0 grain" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 pt-40 pb-32 min-h-screen flex flex-col justify-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="max-w-3xl"
        >
          <div className="flex items-center text-cream/80 eyebrow mb-8" data-testid="hero-overline">
            {t.hero.overline}
          </div>

          <h1 className="font-display text-cream text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.04] tracking-tight">
            <span className="block">{t.hero.titleA}</span>
            <span className="block italic text-sage-light">{t.hero.titleB}</span>
          </h1>

          <p
            className="mt-8 text-cream/85 text-lg sm:text-xl leading-relaxed max-w-2xl font-light"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#newsletter"
              className="btn-primary inline-flex items-center gap-2 group"
              data-testid="hero-cta-primary"
            >
              {t.hero.ctaPrimary}
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <a
              href="#services"
              className="text-cream/90 hover:text-cream link-underline text-sm tracking-wide"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute left-1/2 -translate-x-1/2 bottom-10 flex flex-col items-center gap-2 text-cream/70"
        >
          <span className="kbd uppercase">{t.hero.scroll}</span>
          <ArrowDown size={16} className="animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
}

/* ---------- Vision ---------- */
function Vision({ t }) {
  return (
    <section
      id="vision"
      className="relative py-24 sm:py-32 lg:py-40"
      data-testid="vision-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <motion.div
            className="lg:col-span-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
          >
            <div className="flex items-center text-sage eyebrow">
              {t.vision.overline}
            </div>
            <div className="mt-10 hidden lg:block">
              <div
                className="w-32 h-32 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle at 30% 30%, #D5C5B9 0%, #C8A8A0 60%, transparent 70%)",
                }}
              />
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={1}
          >
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.15] tracking-tight text-ink">
              {t.vision.title}
            </h2>
            <p className="mt-8 text-ink/75 text-lg leading-relaxed max-w-2xl font-light">
              {t.vision.body}
            </p>

            <div className="mt-12 flex items-center gap-6 text-sm text-mutedfg">
              <div className="flex items-center gap-2" data-testid="vision-location">
                <MapPin size={16} className="text-sage" />
                Kristineberg · Sverige
              </div>
              <div className="w-px h-4 bg-line" />
              <div>Aktiebolag (AB)</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------- About / Judith ---------- */
function About({ t }) {
  return (
    <section
      id="about"
      className="relative py-24 sm:py-32 lg:py-40 overflow-hidden"
      data-testid="about-section"
    >
      <div
        aria-hidden
        className="absolute -top-32 right-[-160px] w-[420px] h-[420px] rounded-full opacity-40 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(213,197,185,0.45) 0%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-[-120px] left-[-120px] w-[360px] h-[360px] rounded-full opacity-30 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(163,181,153,0.4) 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-3xl mx-auto px-5 sm:px-8 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center text-sage eyebrow">
            {t.about.overline}
          </div>

          <h2
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-ink"
            data-testid="about-name"
          >
            {t.about.name}
          </h2>
          <p className="mt-3 font-display italic text-xl sm:text-2xl text-sage-dark">
            {t.about.role}
          </p>

          <p
            className="mt-8 text-ink/80 text-lg leading-relaxed font-light"
            data-testid="about-lead"
          >
            {t.about.lead}
          </p>

          <p
            className="mt-5 text-ink/75 leading-relaxed font-light"
            data-testid="about-focus"
          >
            {t.about.focus}
          </p>

          <div className="mt-12">
            <div className="eyebrow text-mutedfg mb-4">
              {t.about.topicsLabel}
            </div>
            <ul
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2.5"
              data-testid="about-topics"
            >
              {t.about.topics.map((c) => (
                <li
                  key={c}
                  className="flex gap-3 text-sm text-ink/75 leading-relaxed"
                >
                  <span
                    aria-hidden
                    className="mt-[7px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-sage"
                  />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <p
            className="mt-10 text-ink/75 leading-relaxed font-light"
            data-testid="about-philosophy"
          >
            {t.about.philosophy}
          </p>

          <p
            className="mt-5 text-ink/75 leading-relaxed font-light"
            data-testid="about-north"
          >
            {t.about.north}
          </p>

          <div className="mt-10">
            <div className="eyebrow text-mutedfg mb-3">
              {t.about.credsLabel}
            </div>
            <p
              className="text-sm text-ink/75 leading-relaxed font-light"
              data-testid="about-creds"
            >
              {t.about.creds}
            </p>
          </div>

          <blockquote className="mt-14 relative pl-7 border-l-2 border-sage/40 max-w-2xl">
            <p className="font-display italic text-xl sm:text-2xl leading-snug text-ink/85">
              “{t.about.quote}”
            </p>
            <footer className="mt-3 eyebrow text-mutedfg">
              — Judith Seel
            </footer>
          </blockquote>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------- Services ---------- */
function Services({ t }) {
  return (
    <section
      id="services"
      className="relative py-24 sm:py-32 lg:py-40 bg-muted/40"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <div className="flex items-center text-sage eyebrow">
            {t.services.overline}
          </div>
          <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight text-ink">
            {t.services.title}
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {t.services.items.map((s, i) => {
            const Icon = SERVICE_ICONS[i] || Laptop2;
            return (
              <motion.div
                key={s.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={i}
                className="service-card glass-light rounded-3xl p-7 sm:p-8"
                data-testid={`service-card-${i}`}
              >
                <span className="num">0{i + 1}</span>
                <div className="w-11 h-11 rounded-full bg-sage/15 flex items-center justify-center text-sage">
                  <Icon size={20} strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-display text-2xl text-ink leading-tight">
                  {s.name}
                </h3>
                <p className="mt-3 text-sm text-ink/70 leading-relaxed font-light">
                  {s.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mt-16 relative rounded-3xl overflow-hidden h-64 sm:h-80"
        >
          <img
            src={SUPPORT_DOG_1}
            alt="Husky in nordic forest"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-ink/15 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 sm:bottom-8 sm:left-8">
            <div className="font-display italic text-cream text-2xl sm:text-3xl max-w-xl leading-snug">
              “{t.vision.title.split(" — ")[0]}.”
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------- Newsletter ---------- */
function Newsletter({ t, lang, openLegal }) {
  const [email, setEmail] = useState("");
  const [botcheck, setBotcheck] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (botcheck) return; // honeypot triggered
    if (!isValidEmail(email)) {
      setStatus({ state: "error", msg: t.newsletter.invalid });
      return;
    }
    setLoading(true);
    try {
      const data = await submitNewsletter(email, lang);
      if (data?.already_subscribed) {
        setStatus({ state: "success", msg: t.newsletter.already });
      } else {
        setStatus({ state: "success", msg: t.newsletter.success });
      }
      setEmail("");
    } catch (err) {
      setStatus({
        state: "error",
        msg: formatApiErrorDetail(err.response?.data?.detail) || t.newsletter.error,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      className="relative py-24 sm:py-32 lg:py-40"
      data-testid="newsletter-section"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-10 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
        >
          <div className="flex items-center justify-center text-sage eyebrow">
            {t.newsletter.overline}
          </div>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight text-ink">
            {t.newsletter.title}
          </h2>
          <p className="mt-5 text-ink/70 max-w-xl mx-auto font-light">
            {t.newsletter.desc}
          </p>

          <form
            onSubmit={submit}
            className="mt-12 max-w-xl mx-auto"
            data-testid="newsletter-form"
          >
            {/* honeypot — hidden, should remain empty */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={botcheck}
              onChange={(e) => setBotcheck(e.target.value)}
              style={{ position: "absolute", left: "-9999px", opacity: 0 }}
              aria-hidden="true"
            />
            <div className="flex items-end gap-4">
              <input
                type="email"
                inputMode="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.newsletter.placeholder}
                className="input-line text-center sm:text-left"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="btn-primary inline-flex items-center gap-2 shrink-0 disabled:opacity-60"
                data-testid="newsletter-submit-button"
              >
                {loading ? "..." : t.newsletter.button}
                <Send size={16} />
              </button>
            </div>

            <AnimatePresence mode="wait">
              {status.state !== "idle" && (
                <motion.div
                  key={status.msg}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className={`mt-5 inline-flex items-center gap-2 text-sm ${
                    status.state === "success" ? "text-sage-dark" : "text-rose-deep"
                  }`}
                  data-testid="newsletter-status"
                >
                  {status.state === "success" ? (
                    <CheckCircle2 size={16} />
                  ) : (
                    <AlertCircle size={16} />
                  )}
                  {status.msg}
                </motion.div>
              )}
            </AnimatePresence>

            <p
              className="mt-6 text-xs text-mutedfg leading-relaxed max-w-lg mx-auto font-light"
              data-testid="newsletter-consent"
            >
              {t.newsletter.consent.before}
              <button
                type="button"
                onClick={() => openLegal && openLegal("privacy")}
                className="link-underline text-sage-dark hover:text-ink transition-colors"
                data-testid="newsletter-consent-link"
              >
                {t.newsletter.consent.link}
              </button>
              {t.newsletter.consent.after}
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------- Contact ---------- */
function Contact({ t, lang }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [botcheck, setBotcheck] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (botcheck) return; // honeypot triggered
    if (!form.name.trim() || !form.message.trim()) {
      setStatus({ state: "error", msg: t.contact.required });
      return;
    }
    if (!isValidEmail(form.email)) {
      setStatus({ state: "error", msg: t.newsletter.invalid });
      return;
    }
    setLoading(true);
    try {
      await submitContact({ ...form, language: lang });
      setStatus({ state: "success", msg: t.contact.success });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        msg: formatApiErrorDetail(err.response?.data?.detail) || t.contact.error,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 sm:py-32 lg:py-40 bg-ink text-cream overflow-hidden"
      data-testid="contact-section"
    >
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, #708265 0%, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #D5C5B9 0%, transparent 70%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          className="lg:col-span-5"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
        >
          <div className="flex items-center text-sage-light eyebrow">
            {t.contact.overline}
          </div>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl font-light leading-tight">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-cream/75 font-light max-w-md">{t.contact.desc}</p>

          {lang === "sv" && t.contact.languageNote && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 max-w-md rounded-2xl border border-sage-light/30 bg-sage-light/[0.08] px-5 py-4 text-sm text-cream/85 font-light leading-relaxed flex gap-3"
              data-testid="contact-language-note"
            >
              <span
                aria-hidden="true"
                className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sage-light/15 text-sage-light font-display text-base"
              >
                Sv
              </span>
              <span>{t.contact.languageNote}</span>
            </motion.div>
          )}

          <div className="mt-10 space-y-4 text-sm">
            <a
              href="mailto:info@dogcenter-lappland.se"
              className="flex items-center gap-3 text-cream/90 hover:text-cream link-underline w-fit"
              data-testid="contact-email-link"
            >
              <Mail size={16} className="text-sage-light" />
              info@dogcenter-lappland.se
            </a>
            <div className="flex items-center gap-3 text-cream/80">
              <MapPin size={16} className="text-sage-light" />
              Kristineberg, Sverige
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          className="lg:col-span-7 space-y-7"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          custom={1}
          data-testid="contact-form"
        >
          {/* honeypot */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={botcheck}
            onChange={(e) => setBotcheck(e.target.value)}
            style={{ position: "absolute", left: "-9999px", opacity: 0 }}
            aria-hidden="true"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
            <div>
              <label className="eyebrow text-cream/55 block mb-1">
                {t.contact.name}
              </label>
              <input
                value={form.name}
                onChange={onChange("name")}
                className="input-line on-dark"
                placeholder={t.contact.name}
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="eyebrow text-cream/55 block mb-1">
                {t.contact.email}
              </label>
              <input
                type="email"
                value={form.email}
                onChange={onChange("email")}
                className="input-line on-dark"
                placeholder="email@example.com"
                data-testid="contact-email-input"
              />
            </div>
          </div>

          <div>
            <label className="eyebrow text-cream/55 block mb-1">
              {t.contact.message}
            </label>
            <textarea
              rows={4}
              value={form.message}
              onChange={onChange("message")}
              className="input-line on-dark resize-none"
              placeholder={t.contact.message}
              data-testid="contact-message-input"
            />
          </div>

          <div className="flex flex-wrap items-center gap-5 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-cream text-ink px-7 py-3 font-medium hover:bg-sage-light transition-colors disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {loading ? "..." : t.contact.button}
              <ArrowRight size={16} />
            </button>

            <AnimatePresence mode="wait">
              {status.state !== "idle" && (
                <motion.div
                  key={status.msg}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  className={`inline-flex items-center gap-2 text-sm ${
                    status.state === "success" ? "text-sage-light" : "text-rose-deep"
                  }`}
                  data-testid="contact-status"
                >
                  {status.state === "success" ? (
                    <CheckCircle2 size={16} />
                  ) : (
                    <AlertCircle size={16} />
                  )}
                  {status.msg}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

/* ---------- Footer ---------- */
function Footer({ t, openLegal }) {
  const year = new Date().getFullYear();
  return (
    <footer
      className="bg-ink text-cream/80 border-t border-white/5"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img
                src={LOGO_URL}
                alt="DogCenter Lappland"
                className="h-10 w-10 object-contain"
              />
              <div className="font-brand text-base sm:text-lg tracking-wide">
                <span className="font-bold text-brand-olive">DOGCENTER</span>{" "}
                <span className="font-semibold text-brand-sage">Lappland</span>{" "}
                <span className="font-semibold text-brand-sage/85">AB</span>
              </div>
            </div>
            <p className="mt-4 text-sm text-cream/55 max-w-md font-light">
              {t.footer.tagline}
            </p>
          </div>

          <div className="flex items-center gap-5">
            <a
              href="https://www.instagram.com/dogcenterlappland"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/5 hover:border-white/30 transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram size={16} />
            </a>
            <a
              href="mailto:info@dogcenter-lappland.se"
              aria-label="Email"
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/5 hover:border-white/30 transition-colors"
              data-testid="footer-email"
            >
              <Mail size={16} />
            </a>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-xs text-cream/45">
          <div>
            © {year} DogCenter Lappland AB · {t.footer.rights}
          </div>
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => openLegal("imprint")}
              className="link-underline hover:text-cream transition-colors"
              data-testid="footer-imprint-link"
            >
              {t.footer.legal}
            </button>
            <button
              type="button"
              onClick={() => openLegal("privacy")}
              className="link-underline hover:text-cream transition-colors"
              data-testid="footer-privacy-link"
            >
              {t.footer.privacy}
            </button>
            <a
              href="mailto:info@dogcenter-lappland.se"
              className="link-underline hover:text-cream transition-colors"
              data-testid="footer-email-text"
            >
              info@dogcenter-lappland.se
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ---------- Cookie Banner ---------- */
const COOKIE_STORAGE_KEY = "dcl_cookie_consent_v1";

function CookieBanner({ t, openLegal }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const ack = window.localStorage.getItem(COOKIE_STORAGE_KEY);
      if (!ack) {
        const tm = setTimeout(() => setVisible(true), 1200);
        return () => clearTimeout(tm);
      }
    } catch (_) {
      setVisible(true);
    }
  }, []);

  const accept = () => {
    try {
      window.localStorage.setItem(COOKIE_STORAGE_KEY, "acknowledged");
    } catch (_) {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 32, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[55] bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 max-w-md"
          role="dialog"
          aria-label={t.cookie.title}
          data-testid="cookie-banner"
        >
          <div className="rounded-2xl bg-ink/95 backdrop-blur-md text-cream shadow-deep border border-white/10 p-5 sm:p-6">
            <div className="flex items-center gap-2 eyebrow text-sage-light mb-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-sage-light" />
              {t.cookie.title}
            </div>
            <p className="text-sm leading-relaxed text-cream/80 font-light">
              {t.cookie.body}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                onClick={accept}
                className="inline-flex items-center gap-2 rounded-full bg-cream text-ink px-5 py-2 text-sm font-medium hover:bg-sage-light transition-colors"
                data-testid="cookie-accept"
              >
                {t.cookie.accept}
              </button>
              <button
                onClick={() => openLegal && openLegal("privacy")}
                className="text-sm text-cream/70 hover:text-cream link-underline"
                data-testid="cookie-more"
              >
                {t.cookie.more}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------- App ---------- */
export default function App() {
  const [lang, setLang] = useState("de");
  const [scrolled, setScrolled] = useState(false);
  const [legalKind, setLegalKind] = useState(null);

  const t = useMemo(() => dict[lang] || dict.de, [lang]);
  // Note: `dict` is an imported module-level constant and intentionally
  // omitted from deps — including it has no effect since its identity is stable.

  useEffect(() => {
    document.documentElement.lang = lang === "sv" ? "sv" : lang;
  }, [lang]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="bg-cream text-ink min-h-screen" data-testid="app-root">
      <Nav lang={lang} setLang={setLang} t={t} scrolled={scrolled} />
      <Hero t={t} />
      <Vision t={t} />
      <About t={t} />
      <Services t={t} />
      <Newsletter t={t} lang={lang} openLegal={(kind) => setLegalKind(kind)} />
      <Contact t={t} lang={lang} />
      <Footer t={t} openLegal={(kind) => setLegalKind(kind)} />
      <LegalModal
        open={!!legalKind}
        kind={legalKind}
        lang={lang}
        onClose={() => setLegalKind(null)}
      />
      <CookieBanner t={t} openLegal={(kind) => setLegalKind(kind)} />
    </div>
  );
}
